import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import API from '@services/API';
import endpoints from '@constants/configs/endpoints';
import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@modules/common-utils-services/axiosUtil';
import { startSpinner, stopSpinner, setError } from '@actions/common/actionCreators';
import { ContentIds, ISubmitHandler } from '../constants/media.interface';
import { HTML_CODE, MEDIA_CODE, TRANSCRIPTION_CODE } from '../constants/media.constants';

interface IUseMediaFormHandlerProps {
    defaultValues?: {
        interactionId?: number;
    };
    fileUrls?: {
        media: string;
        html: string;
        transcription: string;
    };
}

const useMediaFormHandler = ({defaultValues, fileUrls: initialFileUrls}: IUseMediaFormHandlerProps) => {
    const dispatch = useDispatch();
    const [ mediaNotFoundError, setMediaNotFoundError ] = useState('');
    const [fileUrls, setFileUrls] = useState(initialFileUrls || {media: '', html: '', transcription: ''});
    const mediaFormSchema = z.object({
        interactionId: z.number().min(1, { message: 'cap.mediaAccess.error.interactionIdRequired' })
    });

    const formData = useForm({
        defaultValues: defaultValues || {
            interactionId: 0,
        },
        resolver: zodResolver(mediaFormSchema),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const onSubmitHandler = async ({ setContentData, contactId, fromAccountActivity = false }: ISubmitHandler) => {
        const url = endpoints.MEDIA_ACCESS.processReplacements({ contactId });
        dispatch(startSpinner('cap.mediaAccess.loading'));
        try {
            const response = await api.get(url);
            const mediaFiles = response?.data?.data?.content || [];
            const media = {};
            const urls = { media: '', html: '', transcription: '' };
            for (const item of mediaFiles) {
                const { mediaType, isMediaOrHtml, url, data } = item;
                if (mediaType === MEDIA_CODE && isMediaOrHtml) {
                    media['mediaUrl'] = url;
                    urls['media'] = url;
                } else if (mediaType === HTML_CODE && (!!data || isMediaOrHtml)) {
                    media['interaction'] = data;
                    urls['html'] = url;
                } else if (mediaType === TRANSCRIPTION_CODE && (!!data || isMediaOrHtml)) {
                    media['transcription'] = data;
                    urls['transcription'] = url;
                }
            };
            setContentData({
                id: ContentIds.DISPLAY_MEDIA,
                props: {
                    interactionId: contactId,
                    mediaData: media,
                    fileUrls: urls,
                    fromAccountActivity,
                    mediaAccessType: mediaFiles[0]?.mediaAccessType,
                    downloadMedia: mediaFiles[0]?.downloadMedia,
                }
            });
            setFileUrls(urls);
        } catch (error) {
            setMediaNotFoundError(fromAccountActivity ? 'cap.mediaAccess.unableToLocate.errorWithParams' : 'cap.mediaAccess.unableToLocate.error');
        } finally {
            dispatch(stopSpinner());
        }
    };

    const onDownloadMedia = () => {
        if (!fileUrls?.media) {
            return;
        }
        const url = endpoints.MEDIA_DOWNLOAD.processReplacements({ downloadUrl: fileUrls.media });
        API.downloadFile(url, dispatch, { setError, startSpinner, stopSpinner }, true);
    };
    
    const onDownloadHtml = () => {
        if (!fileUrls?.html) {
            return;
        }
        const url = endpoints.MEDIA_DOWNLOAD.processReplacements({ downloadUrl: fileUrls.html });
        API.downloadFile(url, dispatch, { setError, startSpinner, stopSpinner }, true);
    };

    const onDownloadTranscription = () => {
        if (!fileUrls?.transcription) {
            return;
        }
        const url = endpoints.MEDIA_DOWNLOAD.processReplacements({ downloadUrl: fileUrls.transcription });
        API.downloadFile(url, dispatch, { setError, startSpinner, stopSpinner }, true);
    };

    return {
        ...formData,
        onSubmitHandler,
        onDownloadMedia,
        onDownloadHtml,
        onDownloadTranscription,
        mediaNotFoundError,
        setMediaNotFoundError,
    };
};

export default useMediaFormHandler;